import { useState, useEffect, createContext, useContext, useMemo } from 'react'
import {
  Organization,
  OrgTemplate,
  OrgAgent,
  resolveTenant,
  getCurrentOrg,
  getOrgTemplates,
  getOrgAgents,
  hasLevel2Features
} from '@/lib/tenant'

// ============================================
// TENANT HOOK & CONTEXT
// ============================================

interface TenantContextValue {
  org: Organization | null
  loading: boolean
  error: string | null
  isLevel2: boolean
  orgTemplates: OrgTemplate[]
  orgAgents: OrgAgent[]
  refreshTenant: () => Promise<void>
}

const TenantContext = createContext<TenantContextValue | null>(null)

/**
 * Hook para acceder al tenant actual
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext)

  // Si no hay contexto, usar el hook directamente (para componentes fuera del provider)
  const [org, setOrg] = useState<Organization | null>(getCurrentOrg())
  const [loading, setLoading] = useState(!org)
  const [error, setError] = useState<string | null>(null)
  const [orgTemplates, setOrgTemplates] = useState<OrgTemplate[]>([])
  const [orgAgents, setOrgAgents] = useState<OrgAgent[]>([])

  const loadTenant = async () => {
    try {
      setLoading(true)
      setError(null)

      const resolved = await resolveTenant()
      setOrg(resolved)

      // Si es nivel 2, cargar templates y agents de la org
      if (hasLevel2Features(resolved)) {
        const [templates, agents] = await Promise.all([
          getOrgTemplates(resolved.id),
          getOrgAgents(resolved.id)
        ])
        setOrgTemplates(templates)
        setOrgAgents(agents)
      }

    } catch (err) {
      console.error('Error loading tenant:', err)
      setError('Error al cargar la configuración')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!context) {
      loadTenant()
    }
  }, [])

  // Si hay contexto, usarlo
  if (context) {
    return context
  }

  return {
    org,
    loading,
    error,
    isLevel2: org ? hasLevel2Features(org) : false,
    orgTemplates,
    orgAgents,
    refreshTenant: loadTenant
  }
}

/**
 * Provider del contexto de tenant (opcional, para optimizar)
 */
export { TenantContext }

// Configuraciones de branding por slug (para carga instantánea)
const BRAND_CONFIGS: Record<string, {
  logoSidebar: string
  logoLogin: string
  loginBackground: string
  primaryColor: string
  secondaryColor: string
  loginTitle: string
  loginSubtitle: string
  orgName: string
  favicon?: string
  dashboardBanner?: string | null  // null = no mostrar banner
  dashboardBannerHeight?: string  // Altura del banner (ej: 'h-48', 'h-64')
  sidebarColor?: string  // Color de fondo del sidebar
  sidebarForeground?: string  // Color del texto del sidebar
}> = {
  'kin-riman': {
    logoSidebar: '/brands/kin-riman/logogris.png',
    logoLogin: '/aspronet-media/labs.png',
    loginBackground: '/brands/kin-riman/logogris.png',
    primaryColor: '#2563eb',
    secondaryColor: '#7c3aed',
    loginTitle: 'Bienvenido a KIN',
    loginSubtitle: 'Plataforma de IA para tu negocio MLM',
    orgName: 'KIN',
    dashboardBanner: '/brands/kin-riman/KIN LABS.jpg',
    dashboardBannerHeight: 'h-48',
  },
  'all-stars': {
    logoSidebar: '/brands/all-stars/ALL STARS CONNECT HORIZONTAL FINAL (1).png',
    logoLogin: '/brands/all-stars/ALL STARS CONNECT CENTRO FINAL (1).png',
    loginBackground: '/brands/all-stars/ALL STARS CONNECT CENTRO FINAL (1).png',
    primaryColor: '#f59e0b',
    secondaryColor: '#ef4444',
    loginTitle: 'Bienvenido a All Stars Connect',
    loginSubtitle: 'Tu plataforma de IA personalizada',
    orgName: 'All Stars Connect',
    favicon: '/brands/all-stars/ALL STARS CONNECT CENTRO FINAL (1).png',
    dashboardBanner: null,
    sidebarColor: '#FFFFFF',
    sidebarForeground: '#111827',
  },
  'aspronet-labs': {
    logoSidebar: '/brands/aspronet-labs/logoside.png',
    logoLogin: '/brands/aspronet-labs/logo-login.png',
    loginBackground: '/brands/aspronet-labs/login.png',
    primaryColor: '#6366f1',
    secondaryColor: '#8b5cf6',
    loginTitle: 'Bienvenido a Aspronet Labs',
    loginSubtitle: 'La Revolución del Network Marketing',
    orgName: 'Aspronet Labs',
    favicon: '/brands/aspronet-labs/favicon.ico',
    dashboardBanner: '/brands/aspronet-labs/bannerdash.png',
    dashboardBannerHeight: 'h-72',
  },
  'gln-tapia': {
    logoSidebar: '/brands/gln-tapia/Logo Glow life nation color con transparencia.png',
    logoLogin: '/brands/gln-tapia/Logo Glow life nation color con transparencia.png',
    loginBackground: '/brands/gln-tapia/GLN logo 500 x 500 size cover.jpg',
    primaryColor: '#577934',
    secondaryColor: '#3d5524',
    loginTitle: 'Bienvenido a GLN',
    loginSubtitle: 'Tu plataforma de IA personalizada',
    orgName: 'GLN | IA',
    favicon: '/brands/gln-tapia/GLN logo 500 x 500 size cover.jpg',
    dashboardBanner: '/brands/gln-tapia/GLN logo 1920 x 640 cover.jpg',
  },
  '1house': {
    logoSidebar: '/brands/1house/1housefav.png',
    logoLogin: '/brands/1house/1housefav.png',
    loginBackground: '/brands/1house/1housefav.png',
    primaryColor: '#1e40af',
    secondaryColor: '#0ea5e9',
    loginTitle: 'Bienvenido a 1House',
    loginSubtitle: 'Tu plataforma de IA',
    orgName: '1House',
    favicon: '/brands/1house/1housefav.png',
    dashboardBanner: '/brands/1house/Banner N.jpg',
    dashboardBannerHeight: 'h-48',
  },
  'aurum': {
    logoSidebar: '/brands/aurum/aurm.svg',
    logoLogin: '/brands/aurum/aurm.svg',
    loginBackground: '/brands/aurum/background.jpg',
    primaryColor: '#d4af37',
    secondaryColor: '#111827',
    loginTitle: 'Welcome to Aurum',
    loginSubtitle: 'Your personalized AI platform',
    orgName: 'Aurum',
    favicon: '/brands/aurum/faviconn.png',
    dashboardBanner: '/brands/aurum/aurum-flow.png',
    dashboardBannerHeight: 'h-48',
  },
  'test-56': {
    logoSidebar: '/transparent.png',
    logoLogin: '/transparent.png',
    loginBackground: '/aspronet-media/Fondo-aspronet.png',
    primaryColor: '#2563eb',
    secondaryColor: '#7c3aed',
    loginTitle: 'Bienvenido a Test 56',
    loginSubtitle: 'Tu plataforma de IA',
    orgName: 'Test 56',
    dashboardBanner: null,
    dashboardBannerHeight: 'h-48',
  },
  'aspronet': {
    logoSidebar: '/logogris.png',
    logoLogin: '/aspronet-media/labs.png',
    loginBackground: '/aspronet-media/Fondo-aspronet.png',
    primaryColor: '#6366f1',
    secondaryColor: '#8b5cf6',
    loginTitle: 'Bienvenido a Aspronet',
    loginSubtitle: 'Innovación en IA para tu negocio',
    orgName: 'Aspronet',
    dashboardBanner: '/bannerhome.png',
    dashboardBannerHeight: 'h-64',
  },
  'efex': {
    logoSidebar: '/brands/efex/efexlogo.png',
    logoLogin: '/brands/efex/efexlogo.png',
    loginBackground: '/brands/efex/efexlogo.png',
    primaryColor: '#E31E24',       // Rojo EFEX
    secondaryColor: '#B71C1C',     // Rojo oscuro
    loginTitle: 'Bienvenido a EFEX',
    loginSubtitle: 'Tu plataforma de IA personalizada',
    orgName: 'EFEX',
    favicon: '/brands/efex/efexlogo.png',
    dashboardBanner: null,
    dashboardBannerHeight: 'h-48',
  },
  'lumina': {
    logoSidebar: '/brands/lumina/logo.svg',
    logoLogin: '/brands/lumina/logo.svg',
    loginBackground: '/brands/lumina/logo.svg',
    primaryColor: '#2563eb',
    secondaryColor: '#7c3aed',
    loginTitle: 'Bienvenido a Lumina',
    loginSubtitle: 'Plataforma de IA para tu negocio MLM',
    orgName: 'Lumina',
    favicon: '/brands/lumina/logo.svg',
    dashboardBanner: '/brands/lumina/bannerhome.svg',
    dashboardBannerHeight: 'h-48',
  },
}

/**
 * Hook simplificado para obtener solo el branding
 * Usa config local para carga instantánea, no depende de BD
 */
export function useBranding() {
  const extractBrandSlug = () => {
    const envSlug = import.meta.env.VITE_TENANT_SLUG
    if (envSlug) return envSlug

    const hostname = window.location.hostname
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      /^\d+\.\d+\.\d+\.\d+$/.test(hostname)
    ) {
      return 'aspronet'
    }

    const parts = hostname.split('.')
    if (parts.length >= 3) {
      return parts[0]
    }

    return hostname
  }

  const slug = extractBrandSlug()

  // Buscar config local, fallback a kin-riman
  const config = BRAND_CONFIGS[slug] || BRAND_CONFIGS['kin-riman']

  // Actualizar título y favicon del documento
  useEffect(() => {
    document.title = config.orgName

    // Actualizar favicon (usa logoSidebar como fallback)
    const faviconUrl = config.favicon || config.logoSidebar
    if (faviconUrl) {
      const link = document.querySelector("link[rel='icon']") as HTMLLinkElement
      if (link) {
        link.href = faviconUrl
      }
    }
  }, [config.orgName, config.favicon, config.logoSidebar])

  return {
    loading: false,
    logoSidebar: config.logoSidebar,
    logoLogin: config.logoLogin,
    loginBackground: config.loginBackground,
    primaryColor: config.primaryColor,
    secondaryColor: config.secondaryColor,
    loginTitle: config.loginTitle,
    loginSubtitle: config.loginSubtitle,
    orgName: config.orgName,
    favicon: config.favicon,
    dashboardBanner: config.dashboardBanner,
    dashboardBannerHeight: config.dashboardBannerHeight || 'h-48',
    sidebarColor: config.sidebarColor,
    sidebarForeground: config.sidebarForeground || (config.sidebarColor ? '#FFFFFF' : undefined),
    slug,
  }
}

/**
 * Hook para verificar si el usuario tiene acceso a features de nivel 2
 */
export function useLevel2Features() {
  const { org, isLevel2, orgTemplates, orgAgents, loading } = useTenant()

  return {
    loading,
    hasLevel2: isLevel2,
    templates: orgTemplates,
    agents: orgAgents,
    orgId: org?.id
  }
}
